import { useEffect } from 'react';

import styled from '@emotion/styled';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import useRiskSettings from '../../../hooks/dashboard/risk/useRiskSettings';
import { RiskThresholds, THRESHOLD_FIELDS } from '../../../models/risk/risk.model';
import { tableFormatDateTimeNG } from '../../../utils/utils';

interface RuleSettingsPanelProps {
  // Called after a save, so the page can re-scan with the new settings.
  onSaved?: () => void;
}

interface FormValues {
  thresholds: Record<keyof RiskThresholds, number>;
  enabled: string[];
}

const toNumbers = (thresholds: RiskThresholds) =>
  Object.fromEntries(
    Object.entries(thresholds).map(([key, value]) => [key, Number(value)])
  ) as Record<keyof RiskThresholds, number>;

const schema = Yup.object({
  thresholds: Yup.object(
    Object.fromEntries(
      THRESHOLD_FIELDS.map(({ key }) => [
        key,
        Yup.number().typeError('Number').required('Required').min(key === 'outlier_min_amount' ? 0 : 1),
      ])
    )
  ).test(
    'critical-above-high',
    'Declines: critical must be above high',
    (value: any) => !value || Number(value.decline_streak_critical) > Number(value.decline_streak_high)
  ),
});

const RuleSettingsPanel: React.FC<RuleSettingsPanelProps> = ({ onSaved }) => {
  const { settings, saving, fetchSettings, saveSettings } = useRiskSettings();

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  if (!settings) return <Muted>Loading settings…</Muted>;

  const defaults = toNumbers(settings.defaults);
  const initialValues: FormValues = {
    thresholds: toNumbers(settings.thresholds),
    enabled: settings.rules
      .map((r) => r.rule)
      .filter((rule) => !settings.disabled_rules.includes(rule)),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      enableReinitialize
      onSubmit={(values) => {
        const disabled = settings.rules
          .map((r) => r.rule)
          .filter((rule) => !values.enabled.includes(rule));
        saveSettings(values.thresholds as unknown as RiskThresholds, disabled, onSaved);
      }}
    >
      {({ errors, dirty, resetForm, setFieldValue, values }) => (
        <Form>
          <Intro>
            Tune each rule using its false-positive rate in the “Rule performance” table. A rule
            that is mostly dismissed is too sensitive. Saving re-scans with the new settings.
            {settings.updated_by && (
              <Muted>
                Last changed by {settings.updated_by}
                {settings.updated_at ? ` · ${tableFormatDateTimeNG(settings.updated_at)}` : ''}
              </Muted>
            )}
          </Intro>

          <SubTitle>Thresholds</SubTitle>
          <Grid>
            {THRESHOLD_FIELDS.map(({ key, label, hint }) => {
              const changed = Number(values.thresholds[key]) !== defaults[key];
              const error = (errors.thresholds as any)?.[key];
              return (
                <FieldBox key={key}>
                  <label htmlFor={`t-${key}`}>{label}</label>
                  <Field id={`t-${key}`} name={`thresholds.${key}`} type='number' step='any' />
                  <Muted>
                    {error ? <ErrorText>{error}</ErrorText> : hint}
                    {changed && !error && (
                      <>
                        {' · '}
                        <ResetLink
                          type='button'
                          onClick={() => setFieldValue(`thresholds.${key}`, defaults[key])}
                        >
                          default {defaults[key]}
                        </ResetLink>
                      </>
                    )}
                  </Muted>
                </FieldBox>
              );
            })}
          </Grid>
          {typeof errors.thresholds === 'string' && <ErrorText>{errors.thresholds}</ErrorText>}

          <SubTitle>Rules that run</SubTitle>
          <RuleGrid>
            {settings.rules.map((rule) => (
              <RuleToggle key={rule.rule}>
                <Field type='checkbox' name='enabled' value={rule.rule} />
                <span>
                  {rule.title}
                  {rule.self_clearing && <Muted>Clears itself when fixed</Muted>}
                </span>
              </RuleToggle>
            ))}
          </RuleGrid>

          <Actions>
            <PrimaryWhiteButton type='button' disabled={!dirty || saving} onClick={() => resetForm()}>
              Discard changes
            </PrimaryWhiteButton>
            <PrimaryBlackButton type='submit' className='large' disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save and re-scan'}
            </PrimaryBlackButton>
          </Actions>
        </Form>
      )}
    </Formik>
  );
};

export default RuleSettingsPanel;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.secondaryTextColor};
  margin-bottom: 16px;
`;

const SubTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.grayTextColor};
  text-transform: uppercase;
  margin: 16px 0 10px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const FieldBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.primaryTextColor};

  & > input {
    border: 1px solid ${({ theme }) => theme.palette.borderColor};
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 14px;
  }
`;

const RuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
`;

const RuleToggle = styled.label`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.primaryTextColor};
  cursor: pointer;
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.errorRed};
`;

const ResetLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.blueColor};
  cursor: pointer;
  text-decoration: underline;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;

  & > button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
